import { useCallback, useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

interface UseAccountMessageSettingsReturn {
  showUserNameInMessages: boolean
  loading: boolean
  saving: boolean
  error: string | null
  refetch: () => Promise<void>
  updateShowUserName: (value: boolean) => Promise<boolean>
}

/**
 * Configurações de mensagens da conta.
 *
 * Lê direto da tabela `accounts` em vez de usar `user.account`: o objeto de conta
 * da sessão vem de um select com campos fixos e fica cacheado no localStorage,
 * o que deixaria o valor defasado.
 */
export function useAccountMessageSettings(): UseAccountMessageSettingsReturn {
  const { user } = useAuth()
  const accountId = user?.account_id
  const [showUserNameInMessages, setShowUserNameInMessages] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchSettings = useCallback(async () => {
    if (!supabase || !accountId) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const { data, error: fetchError } = await supabase
        .from('accounts')
        .select('show_user_name_in_messages')
        .eq('id', accountId)
        .maybeSingle()

      if (fetchError) throw fetchError

      setShowUserNameInMessages(Boolean(data?.show_user_name_in_messages))
    } catch (err: any) {
      console.error('❌ [useAccountMessageSettings] Erro ao carregar configurações:', err)
      setError(err?.message ?? 'Erro ao carregar configurações da conta')
    } finally {
      setLoading(false)
    }
  }, [accountId])

  const updateShowUserName = useCallback(
    async (value: boolean): Promise<boolean> => {
      if (!supabase || !accountId) return false

      const previous = showUserNameInMessages
      setSaving(true)
      setError(null)
      setShowUserNameInMessages(value)

      try {
        const { error: updateError } = await supabase
          .from('accounts')
          .update({ show_user_name_in_messages: value })
          .eq('id', accountId)

        if (updateError) throw updateError
        return true
      } catch (err: any) {
        console.error('❌ [useAccountMessageSettings] Erro ao salvar configuração:', err)
        setError(err?.message ?? 'Erro ao salvar configuração')
        setShowUserNameInMessages(previous)
        return false
      } finally {
        setSaving(false)
      }
    },
    [accountId, showUserNameInMessages]
  )

  useEffect(() => {
    fetchSettings()
  }, [fetchSettings])

  return {
    showUserNameInMessages,
    loading,
    saving,
    error,
    refetch: fetchSettings,
    updateShowUserName
  }
}
